import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  TemplateRef
} from '@angular/core';
import { DARK } from '../../environments/theme';
import { Store } from '@ngrx/store';
import {
  returnAuthDone,
  returnGID,
  returnIsProduction,
  returnLanguageIcon,
  returnText
} from 'src/state/selectors';
import {
  setAuthDone,
  setDynamicError,
  setLanguage,
  setLanguageIcon
} from 'src/state/actions';
import { Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import axios from 'axios';
import { environment } from 'src/environments/environment';
import {
  HelperFunctionsService,
  ToastType
} from 'src/app/services/helper-functions.service';
import { SiToastService } from '@simpl/siemens-brand-ng/toast';
import { Observable, take } from 'rxjs';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit {
  constructor(
    private store: Store,
    private router: Router,
    private modalService: BsModalService,
    private toastService: SiToastService,
    private helper: HelperFunctionsService
  ) {
    this.login$ = store.select(returnAuthDone);
    this.language_icon$ = store.select(returnLanguageIcon);
    this.GID$ = store.select(returnGID);
  }

  modalRef!: BsModalRef;
  login$: Observable<boolean | string>;
  GID$: Observable<string | null>;
  language_icon$: Observable<string | null>;
  dark = DARK;
  myItDigitalAssistant = environment.NG_APP_MYIT_DIGITAL_ASSISTANT;
  reportAnIncident = environment.NG_APP_REPORT_AN_INCIDENT;
  wiki = 'https://wiki.siemens.com/display/SiemensCA/Virtual+Smart+Card';
  returnIsProduction!: boolean;

  ngOnInit(): void {
    this.store
      .select(returnIsProduction)
      .pipe(take(1))
      .subscribe(val => (this.returnIsProduction = val));
    return;
  }

  /**
   * The function `languageSelection` in TypeScript displays a modal using a provided template
   * reference.
   * @param template - The `template` parameter in the `languageSelection` function is of type
   * `TemplateRef<any>`. This means it is a reference to a template that can be used to render content
   * in the Angular application.
   */
  languageSelection(template: TemplateRef<any>): void {
    this.modalRef = this.modalService.show(template);
  }

  /**
   * The function `changeLanguage` sets the language setting, language icon, and stores the language in
   * local storage, with error handling for unsupported languages.
   * @param language - The `language` parameter in the `changeLanguage` function is a string
   * that represents the language setting to be changed to. It is used to update the language setting
   * in the application, store the language setting in the local storage, and display an alert message
   * if the language is not supported.
   */
  changeLanguage(language: string): void {
    try {
      this.modalRef.hide();
      this.store.dispatch(setLanguage({ setting: language }));
      this.store.dispatch(setLanguageIcon({ value: language }));
      localStorage.setItem('language', language);
    } catch (error) {
      alert('Language not supported');
    }
  }

  /**
   * The `logout` function in TypeScript sends a logout request to the backend, updates the
   * authentication status, navigates to the logout page, and displays a success or error toast message
   * based on the response.
   */
  async logout(): Promise<void> {
    await axios({
      method: 'get',
      url: `${environment.NG_APP_BACKEND}/logout`,
      withCredentials: true
    })
      .then(() => {
        this.store.dispatch(setAuthDone({ value: false }));
        this.router.navigate(['logout']);
        this.helper.Toasts(this.toastService, ToastType.SUCCESS, 'Logout_main');
      })
      .catch(error => {
        if (error.response) {
          /**
           * The request was made and the server responded with a status code that falls out of the range of 2xx
           * Show an error message and log the error to the console
           */
          this.store.dispatch(
            setDynamicError({ value: this.helper.extractErrorMessage(error) })
          );
          if (error.response?.status === 400) {
            this.helper.Toasts(this.toastService, ToastType.ERROR, 'IDP_Error');
          }
          this.helper.Toasts(
            this.toastService,
            ToastType.ERROR,
            'Dynamic_Error'
          );
          if (!this.returnIsProduction) {
            console.error(error.response);
          }
        } else if (error.request) {
          /**
           * The request was made but no response was received
           * `error.request` is an instance of XMLHttpRequest in the browser and an instance of http.ClientRequest in node.js
           * Show an error message and log the error to the console
           */
          this.store.dispatch(
            setDynamicError({ value: this.helper.extractErrorMessage(error) })
          );
          this.helper.Toasts(
            this.toastService,
            ToastType.ERROR,
            'Dynamic_Error'
          );
          if (!this.returnIsProduction) {
            console.error(JSON.stringify(error.request));
          }
        } else {
          /**
           * Something happened in setting up the request that triggered an Error
           * Show a generic error message and log the error to the console
           */
          this.store.dispatch(
            setDynamicError({ value: this.helper.extractErrorMessage(error) })
          );
          this.helper.Toasts(
            this.toastService,
            ToastType.ERROR,
            'Dynamic_Error'
          );
          if (!returnIsProduction) {
            console.error('Error', JSON.stringify(error.message));
          }
        }
      });
  }

  /**
   * fetches a language specific message from the store
   * @param id
   * @returns msg
   */
  textFetch(id: string): string {
    let msg = '';
    this.store
      .select(returnText(id))
      .subscribe(val => {
        msg = val;
      })
      .unsubscribe();
    return msg;
  }
}
